import { DATA } from "@/common/data";
import { useThemeTransitionGLUniforms } from "@/lib/useThemeTransitionGL";
import GLText from "@/mono/components/gl/GLText";
import { defaultVertex } from "@/mono/lib/gl/shaderChunks";
import useAnimation from "@/mono/lib/hooks/useAnimation";
import useEvent from "@/mono/lib/hooks/useEvent";
import useLoop from "@/mono/lib/hooks/useLoop";
import { clamp, lerp, map } from "@/mono/lib/math";
import { isMobileLayout } from "@/mono/lib/responsive";
import { pixelsToUnits } from "@/mono/lib/styles/WebGL";
import { useGlobalStore } from "@/mono/store/global";
import { useScrollbar } from "@/mono/third-party/r3f-scroll-rig-custom";
import { OrthographicCamera, RenderTexture } from "@react-three/drei";
import { useThree } from "@react-three/fiber";
import { useGesture } from "@use-gesture/react";
import { useTicker } from "mono/lib/hooks/useRaf";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "styled-components";
import { Color, HalfFloatType, RGBAFormat } from "three";
import { create } from "zustand";
import {
  HOME_HOW_WE_DO_IT_IN,
  HOME_HOW_WE_DO_IT_OUT,
  howWeDoItStore,
} from "../pages/home/HowWeDoIt";
import GlassCard from "./GlassCard";

// Add this near the top of the file with the other stores
export const howWeDoItProgress = create(() => 0);

function HowWeDoItGlassCard({ i, ...props }) {
  const cardTransforms = howWeDoItStore((s) => s.cardTransforms);

  const { transform } = useMemo(
    () => cardTransforms.find((t) => t.i === i),
    [cardTransforms]
  );

  return <GlassCard transform={transform} key={i} i={i} {...props} />;
}

export const minScale = 0.9;
export const howWeDoItX = create(() => 0);
export const howWeDoItScale = create(() => 1);

export const howWeDoItRenderTexture = create(() => null);

const fontAdjust = 0.65;

export default function HowWeDoItScene({ ...props }) {
  const camera = useThree((s) => s.camera);
  const theme = useTheme();
  const texture = useRef();

  const bg = useRef();
  const bgColoredPlane = useRef();

  const text = useRef();
  const scale = 200;
  const size = 256;
  const textBounds = useRef();
  const textWidth = useRef(0);
  const cards = useRef();

  const { __lenis: lenis } = useScrollbar();
  const { onScroll } = useScrollbar();
  const vRef = useRef(0);
  useEffect(
    () => onScroll?.(({ velocity }) => (vRef.current = velocity)),
    [onScroll]
  );

  const _x = useRef(0);
  const isDragging = useRef(false);
  const fontSize = isMobileLayout() ? 4.5 : 6.5;
  const [textPositionY, setTextPositionY] = useState(0);

  const gapInUnits = useRef(0);

  const windowSize = useGlobalStore((s) => s.windowSize);

  useEffect(() => {
    // HINT: gap = x pos + scale x of 1st item - x pos of 2nd item
    gapInUnits.current =
      howWeDoItStore.getState().getPosition(1) -
      (howWeDoItStore.getState().getPosition(0) +
        howWeDoItStore.getState().getWidth(0));
  }, [windowSize.width]);

  function moveToNearestCard() {
    const totalWidth =
      howWeDoItStore.getState().getTotalWidth() +
      gapInUnits.current * (DATA.pages.home.howWeDoIt.cards.length - 1);
    const singleWidth =
      howWeDoItStore.getState().getWidth(0) + gapInUnits.current;

    const currentPosition = Math.abs(_x.current);
    const nearestCardIndex = Math.round(currentPosition / singleWidth);

    const targetPosition = -nearestCardIndex * singleWidth;

    _x.current = clamp(targetPosition, -totalWidth + singleWidth, 0);
  }

  const scaleGroup = useRef();
  const __scale = useRef(1);
  const _scale = useRef(1);
  const { animateIn: animateScaleIn, animateOut: animateScaleOut } =
    useAnimation({
      inParams: {
        onUpdate: (v) => {
          const s = map(v, 0, 1, 1, minScale);

          _scale.current = s;
        },
      },
      outParams: {
        onUpdate: (v) => {
          const s = map(v, 0, 1, 1, minScale);

          _scale.current = s;
        },
      },
    });

  function handleMobileDrag({
    delta,
    dragging,
    velocity,
    direction,
    xy,
    ...props
  }) {
    if (!useGlobalStore.getState().isTabletOrSmallerLayout) return;

    // if (dragging) {
    //   stopLenisWithDelay();
    // } else {
    //   clearStopLenisTimeout();
    //   if (lenis.isStopped) {
    //     lenis.start();
    //   }
    // }

    if (dragging && !isDragging.current) {
      isDragging.current = true;
      animateScaleIn();
    }

    if (!dragging && isDragging.current) {
      isDragging.current = false;
      animateScaleOut();
    }

    const totalWidth =
      howWeDoItStore.getState().getTotalWidth() +
      gapInUnits.current * DATA.pages.home.howWeDoIt.cards.length -
      gapInUnits.current;
    const singleWidth = howWeDoItStore.getState().getWidth(0);

    const velocityMultiplier = 3.7;
    const speed = 3.5;
    const x =
      delta[0] + velocity[0] * (velocityMultiplier * direction[0]) * speed;

    _x.current = clamp(_x.current + x, -totalWidth + singleWidth, 0);

    if (!dragging) {
      moveToNearestCard();
    }
  }

  const gesture = useGesture(
    {
      onDrag: handleMobileDrag,
      // TODO:
      onWheel: handleMobileDrag,
    },
    {
      target: document.querySelector(".how-we-do-it__wrapper"),
      drag: {
        axis: "x",
        preventScroll: true,
        threshold: 6,
        eventOptions: { passive: false },
      },
      wheel: { eventOptions: { passive: false } },
    }
  );

  // FIXME: To properly handle UV, we could create UV's as a attribute based on
  // the bounds of each card, it would also be a plus cause it would work for every model

  useEffect(() => {
    if (!text.current) return;

    async function waitForText() {
      await text.current.isSynced();

      textBounds.current = text.current.getBoundingBox();

      textWidth.current = textBounds.current.max.x - textBounds.current.min.x;
      // FIXME:
      textWidth.current *= scale / 6;
      textWidth.current *= fontAdjust;

      setTextPositionY(props.scale.y * -0.45);
    }

    waitForText();

    return () => {
      gesture?.destroy?.();
      // clearStopLenisTimeout();
    };
  }, [props.scale]);

  /**
   * Set the texture on the background mesh
   */
  useEffect(() => {
    if (!bg.current || !texture.current) return;
    bg.current.material.map = texture.current;
  }, [texture.current]);

  function animateIn() {
    text.current?.animateIn({ delay: 0.4, duration: 0.5 });
  }

  const _vel = useRef(0);
  const speed = 0.015;

  const {
    start: startLoop,
    stop: stopLoop,
    setSpeed,
  } = useLoop(
    ({ progress, delta }) => {
      if (!text.current) return;
      const x = map(progress, 0, 1, textWidth.current, -textWidth.current);

      const lenisVelocityMultiplier = useGlobalStore.getState().isMobileLayout
        ? 0.01
        : 0.025;

      text.current.mesh.position.x = x;

      // FIXME: lenis velocity 0 but not after page transition?

      _vel.current = lerp(
        _vel.current,
        Math.abs(vRef.current) * lenisVelocityMultiplier,
        0.05,
        delta
      );

      setSpeed(speed + _vel.current);
    },
    { initiallyActive: true, speed },
    []
  );

  const { start: startTicker, stop: stopTicker } = useTicker(({ delta }) => {
    if (!useGlobalStore.getState().isTabletOrSmallerLayout || !cards.current)
      return;

    const x = howWeDoItX.getState();

    const lerpAlpha = 0.05;

    const newX = lerp(x, _x.current, lerpAlpha, delta);

    howWeDoItX.setState(newX);
    cards.current.position.x = newX;

    __scale.current = lerp(_scale.current, __scale.current, lerpAlpha, delta);
    scaleGroup.current.scale.setScalar(__scale.current);
    howWeDoItScale.setState(__scale.current);

    // Calculate progress
    const totalWidth =
      howWeDoItStore.getState().getTotalWidth() +
      gapInUnits.current * (DATA.pages.home.howWeDoIt.cards.length - 1);
    const currentProgress =
      Math.abs(newX) / (totalWidth - howWeDoItStore.getState().getWidth(0));
    howWeDoItProgress.setState(clamp(currentProgress, 0, 1));
  });

  useEvent(HOME_HOW_WE_DO_IT_IN, () => {
    animateIn();
    startTicker();
    startLoop();
  });

  function reset() {
    _x.current = 0;
    __scale.current = 1;
    _scale.current = 1;
    howWeDoItX.setState(0);
    howWeDoItScale.setState(1);
    howWeDoItProgress.setState(0);

    if (cards.current) cards.current.position.x = 0;
    if (scaleGroup.current) scaleGroup.current.scale.setScalar(1);
  }

  useEvent(HOME_HOW_WE_DO_IT_OUT, () => {
    //animateOut();
    stopTicker();
    stopLoop();

    reset();
  });

  // Add cleanup effect
  useEffect(() => {
    return () => {
      reset();
    };
  }, []);

  useEffect(() => {
    howWeDoItRenderTexture.setState(texture.current);
  }, [texture.current]);

  const textUniforms = useMemo(
    () => ({
      uColor: {
        value: new Color(theme.theme.howWeDoItTextColor),
        __colorName: "howWeDoItTextColor",
      },
    }),
    []
  );

  const bgUniforms = useMemo(
    () => ({
      uColor: {
        value: new Color(theme.theme.background),
        __colorName: "background",
      },
    }),
    []
  );

  const getTexture = () => texture.current;
  const getBg = () => bgColoredPlane.current;
  const getTextMesh = () => text?.current?.mesh;

  useThemeTransitionGLUniforms(getTextMesh, ["uColor"], textUniforms, false);
  useThemeTransitionGLUniforms(getBg, ["uColor"], bgUniforms, false);

  const bgWidth = useMemo(() => {
    return pixelsToUnits(windowSize.width, camera, windowSize.height) * 1000;
  }, [windowSize.width]);

  return (
    <group>
      <group ref={scaleGroup}>
        <group ref={cards}>
          {DATA.pages.home.howWeDoIt.cards.map((card, i) => (
            <HowWeDoItGlassCard key={i} i={i} getTexture={getTexture} />
          ))}
        </group>
      </group>

      <RenderTexture
        ref={texture}
        samples={0}
        stencilBuffer={false}
        depthBuffer={false}
        generateMipmaps={false}
        width={size}
        height={size}
        format={RGBAFormat} // Specify format
        type={HalfFloatType} // Use 16-bit instead of 32-bit
        anisotropy={1}
      >
        <OrthographicCamera makeDefault {...camera} fov={100} />
        <GLText
          ref={text}
          scale={[scale * fontAdjust, scale, scale]}
          uniforms={textUniforms}
          fontSize={fontSize}
          weight={"Medium"}
          letterSpacing={-0.05}
          position-y={textPositionY}
          material-transparent={true}
        >
          ninenineninenineninenine
        </GLText>
        <mesh
          {...props}
          scale={[bgWidth, props.scale.y * 100, 1]}
          renderOrder={2}
          ref={bgColoredPlane}
        >
          <planeGeometry args={[1, 1]} />
          <shaderMaterial
            uniforms={bgUniforms}
            fragmentShader={`
              uniform vec3 uColor;
              void main() {
                gl_FragColor = vec4(uColor, 1.0);
              }
            `}
            vertexShader={defaultVertex}
          />
        </mesh>
      </RenderTexture>

      <mesh ref={bg} {...props} renderOrder={2}>
        <planeGeometry args={[1, 1]} />
        <meshBasicMaterial depthTest={false} depthWrite={false} />
      </mesh>
    </group>
  );
}
